/**
 * Custom React hook for UTM tracking
 * Manages UTM parameter capture and provides data for forms
 */

import { useEffect, useState } from 'react';
import {
  initializeUTMTracking,
  getUTMDataForForm
} from '../utils/utm';

/**
 * Hook to track and provide UTM data
 * @returns {object} UTM data object with all tracking parameters
 */
const useUTMTracking = () => {
  const [utmData, setUtmData] = useState({
    utm_source: 'direct',
    utm_medium: 'none',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
    gclid: '',
    fbclid: '',
    landing_page_url: '',
    referrer: '',
    utm_captured_at: new Date().toISOString()
  });

  useEffect(() => {
    // Initialize UTM tracking on component mount
    const data = getUTMDataForForm();
    setUtmData(data);

    // Log tracking data (useful for debugging)
    if (process.env.NODE_ENV === 'development') {
      console.log('UTM Data Captured:', data);
    }
  }, []);

  return utmData;
};

/**
 * Hook to track page views with UTM data
 * Useful for GA4 or other analytics integration
 */
const useUTMPageView = () => {
  const utmData = useUTMTracking();

  useEffect(() => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'pageView',
        utm_source: utmData.utm_source,
        utm_medium: utmData.utm_medium,
        utm_campaign: utmData.utm_campaign,
        utm_term: utmData.utm_term,
        utm_content: utmData.utm_content,
        gclid: utmData.gclid,
        fbclid: utmData.fbclid,
        landing_page_url: utmData.landing_page_url,
        referrer: utmData.referrer
      });
    }
  }, [utmData]);

  return utmData;
};

export { useUTMTracking, useUTMPageView };
